import Cookies from 'js-cookie';
import { useCallback } from 'react';

import type { TLoginFormValues } from '~features/auth/ui/LoginModal/form';
import { getLocale, getLocalizedUrl } from '~features/language/model/utils';
import { localeCookieName, localeExpires } from '~i18n/constants';
import { authApi } from '~services/api/auth';
import { userApi } from '~services/api/user';
import { getHost, isDev } from '~utils/host';

export const useLogin = () => {
	const [sendLogin] = authApi.useLoginMutation();
	const [sendGoogleLogin] = authApi.useGoogleLoginMutation();
	const [sendDiscordLogin] = authApi.useDiscordLoginMutation();
	const [getUserPreferences] = userApi.useLazyUserPreferencesQuery();
	const [setUserLanguage] = userApi.useSetUserLanguageMutation();

	const postLogin = useCallback(
		async (userId: string) => {
			const { language } = await getUserPreferences({ userId }).unwrap();
			const locale = getLocale(window.location.host);

			if (language) {
				if (language !== locale && !isDev()) {
					Cookies.set(localeCookieName, language, {
						expires: localeExpires,
						domain: getHost(),
					});
					window.location.href = getLocalizedUrl(
						new URLSearchParams(window.location.search),
						window.location.pathname,
						locale
					);
				}
			} else {
				await setUserLanguage({
					userId,
					language: locale,
				}).unwrap();
			}
		},
		[getUserPreferences, setUserLanguage]
	);
	const loginViaEmail = useCallback(
		async (data: TLoginFormValues) => {
			const response = await sendLogin(data).unwrap();
			await postLogin(response.id);

			return response;
		},
		[postLogin, sendLogin]
	);

	const loginViaGoogle = useCallback(
		async ({ idToken }: { idToken: string }) => {
			const response = await sendGoogleLogin({ idToken }).unwrap();
			await postLogin(response.id);

			return response;
		},
		[postLogin, sendGoogleLogin]
	);

	const loginViaDiscord = useCallback(
		async ({ idToken }: { idToken: string }) => {
			const response = await sendDiscordLogin({ idToken }).unwrap();
			await postLogin(response.id);

			return response;
		},
		[postLogin, sendDiscordLogin]
	);

	return {
		loginViaEmail,
		loginViaGoogle,
		loginViaDiscord,
	};
};
